import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { ReactionType, getUserCardReactions, addCardReaction, removeCardReaction } from '../services/pokemonTcgApi';

interface ReactionStore {
  // State
  userReactions: Map<string, ReactionType[]>;
  isLoading: boolean;
  
  // Actions
  loadUserReactions: (userId: string, cardIds: string[]) => Promise<void>;
  loadUserReactionsForCard: (userId: string, cardId: string) => Promise<void>;
  addReaction: (userId: string, cardId: string, reactionType: ReactionType) => Promise<boolean>;
  removeReaction: (userId: string, cardId: string, reactionType: ReactionType) => Promise<boolean>;
  toggleReaction: (userId: string, cardId: string, reactionType: ReactionType) => Promise<boolean>;
  getUserReactionsForCard: (cardId: string) => ReactionType[];
  hasReaction: (cardId: string, reactionType: ReactionType) => boolean;
  clearUserReactions: () => void;
  
  // Optimistic updates
  optimisticallyUpdateReaction: (cardId: string, reactionType: ReactionType, isAdding: boolean) => void;
  revertOptimisticUpdate: (cardId: string, reactionType: ReactionType, isAdding: boolean) => void;
}

export const useReactionStore = create<ReactionStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    userReactions: new Map(),
    isLoading: false,

    // Load user reactions for multiple cards
    loadUserReactions: async (userId: string, cardIds: string[]) => {
      if (!userId || cardIds.length === 0) return;
      
      try {
        set({ isLoading: true });
        
        const reactions = new Map<string, ReactionType[]>();
        
        // Process in batches to avoid overwhelming the API
        const batchSize = 10;
        for (let i = 0; i < cardIds.length; i += batchSize) {
          const batch = cardIds.slice(i, i + batchSize);
          const batchPromises = batch.map(cardId => getUserCardReactions(userId, cardId));
          
          const batchResults = await Promise.all(batchPromises);
          
          batch.forEach((cardId, index) => {
            const cardReactions = batchResults[index];
            if (cardReactions && cardReactions.length > 0) {
              reactions.set(cardId, cardReactions);
            }
          });
        }
        
        set({ userReactions: reactions });
      } catch (error) {
        console.error('Error loading user reactions:', error);
      } finally {
        set({ isLoading: false });
      }
    },

    // Load user reactions for a single card
    loadUserReactionsForCard: async (userId: string, cardId: string) => {
      if (!userId || !cardId) return;
      
      try {
        const reactions = await getUserCardReactions(userId, cardId);
        
        set(state => {
          const newUserReactions = new Map(state.userReactions);
          if (reactions.length > 0) {
            newUserReactions.set(cardId, reactions);
          } else {
            newUserReactions.delete(cardId);
          }
          return { userReactions: newUserReactions };
        });
      } catch (error) {
        console.error('Error loading user reactions for card:', error);
      }
    },

    // Add a reaction
    addReaction: async (userId: string, cardId: string, reactionType: ReactionType) => {
      try {
        const success = await addCardReaction(userId, cardId, reactionType);
        if (success) {
          // Reload reactions for this card to get the latest state
          await get().loadUserReactionsForCard(userId, cardId);
        }
        return success;
      } catch (error) {
        console.error('Error adding reaction:', error);
        return false;
      }
    },

    // Remove a reaction
    removeReaction: async (userId: string, cardId: string, reactionType: ReactionType) => {
      try {
        console.log('Store: removing reaction:', { userId, cardId, reactionType });
        const success = await removeCardReaction(userId, cardId, reactionType);
        if (success) {
          console.log('Store: removal successful, reloading reactions');
          // Reload reactions for this card to get the latest state
          await get().loadUserReactionsForCard(userId, cardId);
        } else {
          console.error('Store: removal failed');
        }
        return success;
      } catch (error) {
        console.error('Error removing reaction:', error);
        return false;
      }
    },

    // Toggle a reaction (add if not present, remove if present)
    toggleReaction: async (userId: string, cardId: string, reactionType: ReactionType) => {
      const currentReactions = get().getUserReactionsForCard(cardId);
      const hasReaction = currentReactions.includes(reactionType);
      
      if (hasReaction) {
        return await get().removeReaction(userId, cardId, reactionType);
      } else {
        return await get().addReaction(userId, cardId, reactionType);
      }
    },

    // Get user reactions for a specific card
    getUserReactionsForCard: (cardId: string) => {
      const { userReactions } = get();
      return userReactions.get(cardId) || [];
    },

    // Check if user has a specific reaction for a card
    hasReaction: (cardId: string, reactionType: ReactionType) => {
      const reactions = get().getUserReactionsForCard(cardId);
      return reactions.includes(reactionType);
    },

    // Clear all user reactions (useful for logout)
    clearUserReactions: () => {
      set({ userReactions: new Map() });
    },

    // Optimistic update (immediate UI feedback)
    optimisticallyUpdateReaction: (cardId: string, reactionType: ReactionType, isAdding: boolean) => {
      console.log('Optimistic update:', { cardId, reactionType, isAdding });
      
      set(state => {
        const newUserReactions = new Map(state.userReactions);
        const currentReactions = newUserReactions.get(cardId) || [];
        
        console.log('Current reactions before update:', currentReactions);
        
        let updatedReactions = [...currentReactions];
        
        if (isAdding) {
          // Add the reaction if it's not already present
          if (!updatedReactions.includes(reactionType)) {
            // Handle like/dislike exclusivity - they are mutually exclusive
            if (reactionType === 'like') {
              // Remove dislike if present
              updatedReactions = updatedReactions.filter(r => r !== 'dislike');
            } else if (reactionType === 'dislike') {
              // Remove like if present
              updatedReactions = updatedReactions.filter(r => r !== 'like');
            }
            
            updatedReactions.push(reactionType);
            console.log('Added reaction, updated reactions:', updatedReactions);
          }
        } else {
          // Remove the reaction
          updatedReactions = updatedReactions.filter(r => r !== reactionType);
          console.log('Removed reaction, updated reactions:', updatedReactions);
        }
        
        if (updatedReactions.length > 0) {
          newUserReactions.set(cardId, updatedReactions);
        } else {
          newUserReactions.delete(cardId);
        }
        
        console.log('Final optimistic state:', updatedReactions);
        return { userReactions: newUserReactions };
      });
    },

    // Revert optimistic update (if API call fails)
    revertOptimisticUpdate: (cardId: string, reactionType: ReactionType, isAdding: boolean) => {
      // Revert by doing the opposite operation
      get().optimisticallyUpdateReaction(cardId, reactionType, !isAdding);
    }
  }))
);

// Selector hooks for easier component usage
export const useUserReactions = () => useReactionStore(state => state.userReactions);
export const useUserReactionsForCard = (cardId: string) => useReactionStore(state => state.getUserReactionsForCard(cardId));
export const useHasReaction = (cardId: string, reactionType: ReactionType) => useReactionStore(state => state.hasReaction(cardId, reactionType));
export const useReactionActions = () => useReactionStore(state => ({
  loadUserReactions: state.loadUserReactions,
  loadUserReactionsForCard: state.loadUserReactionsForCard,
  addReaction: state.addReaction,
  removeReaction: state.removeReaction,
  toggleReaction: state.toggleReaction,
  optimisticallyUpdateReaction: state.optimisticallyUpdateReaction,
  revertOptimisticUpdate: state.revertOptimisticUpdate,
  clearUserReactions: state.clearUserReactions
}));

// Subscribe to auth changes to clear reactions on logout
import { useAuthStore } from './supabase';

useAuthStore.subscribe(
  (state) => state.user,
  (user) => {
    if (!user) {
      useReactionStore.getState().clearUserReactions();
    }
  }
); 